import type {
  AgentSelection,
  ChatState,
  CompletionItem,
  MessageAttachment,
  ModelSelection,
  ResourceReadResult,
  RootState,
  SessionState,
  ToolCallResult,
  URI,
} from '@microsoft/agent-host-protocol'
import type {BuildInputCompletedActionParams} from './ahp-input-actions'
import type {ProvisioningStatus} from './session-host/resolve-project'
import type {
  ClientTool,
  RelayChatObserver,
  RelayChangesetObserver,
  RelayConnectionProgress,
  RelayModelCallFailure,
  RelaySessionRenameOptions,
  RelayStatus,
  RelayTelemetryObserver,
} from './relay-connection-runtime'
import type {ObservableRelaySink, RelayAcceptedActionListener, RelayEntry} from './relay-entry-types'
import type {RelaySteeringMessage} from './relay-steering-messages'
import type {ContributedInstructionsMetadata} from './pending-message-actions'
import type {SessionApprovalMode} from './permission-mode'
import type {RelayFailure} from './relay-failure'
import {captureRelayReadiness, type RelayReadinessEvidence} from './relay-readiness'

export interface RelayLeaseIdentity {
  /** Product task metadata, when the host has a task id for this session. */
  taskId?: string
  environmentId: string
  sessionId: string
  channel?: string
}

/**
 * A generation-bound relay capability for one mounted surface.
 *
 * The lease deliberately owns the command and observation methods used by the
 * established-session client. Callers do not need to pass a task id back into
 * the registry, so a late cleanup from an older identity cannot mutate a
 * replacement entry.
 */
export interface RelayLease {
  readonly identity: RelayLeaseIdentity
  readonly generation: number
  isCurrent(): boolean
  /** True while this generation still owns the registry entry, even after this lease is released. */
  isGenerationCurrent(): boolean
  release(): void
  cancelConnection(): void
  subscribe(listener: () => void): () => void
  subscribeActions(listener: RelayAcceptedActionListener): () => void
  getSnapshot(): ChatState | undefined
  getStatus(): RelayStatus
  getConnectionProgress?(): RelayConnectionProgress | undefined
  getReadiness?(): RelayReadinessEvidence | undefined
  getReachable(): boolean
  getError(): string | undefined
  getFailure?(): RelayFailure | undefined
  getModelFailures(): Record<string, RelayModelCallFailure> | undefined
  getProvisioning(): ProvisioningStatus | undefined
  getRootState?(): RootState | undefined
  getSessionState(): SessionState | undefined
  getPassive(): boolean
  getPassiveKnown(): boolean
  getSteeringMessages(): readonly RelaySteeringMessage[]
  claimActiveClient(tools: readonly ClientTool[]): Promise<void>
  dispatchTurn(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
    modelText?: string,
  ): string
  queueMessage(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
  ): string
  steerMessage(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    steeringId?: string,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
  ): string
  removeQueuedMessage(id: string): void
  completions(text: string, offset: number): Promise<CompletionItem[]>
  cancelTurn(turnId?: string): boolean
  confirmToolCall(confirmation: {
    approved: boolean
    toolCallId: string
    turnId: string
    resultConfirmation?: boolean
    selectedOptionId?: string
    forSession?: boolean
    chat?: URI
  }): void
  completeToolCall(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void
  renameSession(title: string): void
  renameSession(title: string, options?: RelaySessionRenameOptions): void | Promise<void>
  completeInput(params: BuildInputCompletedActionParams): void
  setApprovalMode(mode: SessionApprovalMode): Promise<void>
  setApproveAll(enabled: boolean): Promise<void>
  setMode(mode: string): Promise<void>
  ensureLive(onReconnect?: () => void): Promise<boolean>
  whenConnected(timeoutMs?: number, provisioningTimeoutMs?: number, signal?: AbortSignal): Promise<void>
  observeChat(channel: string, observer: RelayChatObserver): () => void
  observeChangeset(channel: string, observer: RelayChangesetObserver): () => void
  readResource(uri: string): Promise<ResourceReadResult>
  subscribeTelemetry(observer: RelayTelemetryObserver): Promise<() => void>
}

const relayLeaseEntry = Symbol('relayLeaseEntry')

interface InternalRelayLease extends RelayLease {
  [relayLeaseEntry]: {
    entry: RelayEntry
    registryKey: string
    isReleased: () => boolean
  }
}

export interface RelayLeaseRegistryAccess {
  getRelayEntry(registryKey: string): RelayEntry | undefined
  releaseRelayEntry(registryKey: string, entry: RelayEntry): void
  cancelRelayEntryConnection(registryKey: string, entry: RelayEntry): void
  subscribeRelayRegistry(listener: () => void): () => void
  getRelayStore(registryKey: string): ObservableRelaySink | undefined
  getRelayStatus(registryKey: string): RelayStatus
  getRelayConnectionProgress(registryKey: string): RelayConnectionProgress | undefined
  getRelayReachable(registryKey: string): boolean
  getRelayError(registryKey: string): string | undefined
  getRelayFailure(registryKey: string): RelayFailure | undefined
  getRelayModelFailures(registryKey: string): Record<string, RelayModelCallFailure> | undefined
  getRelayRootState(registryKey: string): RootState | undefined
  getRelaySessionState(registryKey: string): SessionState | undefined
  isRelayPassive(registryKey: string): boolean
  getRelaySteeringMessages(registryKey: string): readonly RelaySteeringMessage[]
  steerRelayMessage(
    registryKey: string,
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    steeringId?: string,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
  ): string
  setRelayApproveAll(registryKey: string, enabled: boolean): Promise<void>
  setRelayApprovalMode(registryKey: string, mode: SessionApprovalMode): Promise<void>
  setRelayMode(registryKey: string, mode: string): Promise<void>
  ensureRelayLive(registryKey: string, onReconnect?: () => void): Promise<boolean>
  whenRelayConnected(
    registryKey: string,
    timeoutMs?: number,
    provisioningTimeoutMs?: number,
    signal?: AbortSignal,
  ): Promise<void>
}

function leaseEntryOrThrow(lease: RelayLease, registry: RelayLeaseRegistryAccess): RelayEntry {
  const internal = lease as InternalRelayLease
  const metadata = internal[relayLeaseEntry]
  const current = registry.getRelayEntry(metadata.registryKey)
  if (metadata.isReleased() || current !== metadata.entry || current.generation !== lease.generation) {
    throw new Error('relay lease is stale')
  }
  return current
}

export function createRelayLease(
  entry: RelayEntry,
  identity: RelayLeaseIdentity,
  registryKey: string,
  registry: RelayLeaseRegistryAccess,
): RelayLease {
  let released = false
  const adopted = captureRelayReadiness('cache')
  let readinessSource = entry.readiness
  let readiness = adopted ? {...readinessSource, adopted} : readinessSource
  const relinquish = (release: (registryKey: string, entry: RelayEntry) => void) => {
    if (released) return
    released = true
    release(registryKey, entry)
  }
  const lease = {
    identity: Object.freeze({...identity}),
    generation: entry.generation,
    isCurrent: () => !released && registry.getRelayEntry(registryKey) === entry,
    isGenerationCurrent: () => registry.getRelayEntry(registryKey) === entry,
    release: () => relinquish(registry.releaseRelayEntry),
    cancelConnection: () => relinquish(registry.cancelRelayEntryConnection),
    subscribe: (listener: () => void) => {
      leaseEntryOrThrow(lease, registry)
      const unsubscribeRegistry = registry.subscribeRelayRegistry(listener)
      const store = registry.getRelayStore(registryKey)
      const unsubscribeStore = store?.subscribe(listener)
      return () => {
        unsubscribeRegistry()
        unsubscribeStore?.()
      }
    },
    subscribeActions: (listener: RelayAcceptedActionListener) => {
      const current = leaseEntryOrThrow(lease, registry)
      current.actionListeners.add(listener)
      return () => {
        current.actionListeners.delete(listener)
      }
    },
    getSnapshot: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayStore(registryKey)?.getSnapshot()
    },
    getStatus: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayStatus(registryKey)
    },
    getConnectionProgress: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayConnectionProgress(registryKey)
    },
    getReadiness: () => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.readiness !== readinessSource) {
        readinessSource = current.readiness
        readiness = adopted ? {...readinessSource, adopted} : readinessSource
      }
      return readiness
    },
    getReachable: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayReachable(registryKey)
    },
    getError: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayError(registryKey)
    },
    getFailure: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayFailure(registryKey)
    },
    getModelFailures: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayModelFailures(registryKey)
    },
    getProvisioning: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayEntry(registryKey)?.provisioning
    },
    getRootState: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayRootState(registryKey)
    },
    getSessionState: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelaySessionState(registryKey)
    },
    getPassive: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.isRelayPassive(registryKey)
    },
    getPassiveKnown: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelayEntry(registryKey)?.passiveKnown ?? false
    },
    getSteeringMessages: () => {
      leaseEntryOrThrow(lease, registry)
      return registry.getRelaySteeringMessages(registryKey)
    },
    claimActiveClient: (tools: readonly ClientTool[]) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.claimActiveClient) throw new Error('active relay cannot claim client-tool ownership')
      return current.runtime.claimActiveClient(tools)
    },
    dispatchTurn: (
      text: string,
      attachments?: MessageAttachment[],
      model?: ModelSelection,
      agent?: AgentSelection,
      contributedInstructions?: ContributedInstructionsMetadata,
      modelText?: string,
    ) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.dispatchTurn) throw new Error('active relay cannot dispatch turns')
      const turnId =
        modelText !== undefined
          ? current.runtime.dispatchTurn(text, attachments, model, agent, contributedInstructions, modelText)
          : contributedInstructions === undefined
            ? current.runtime.dispatchTurn(text, attachments, model, agent)
            : current.runtime.dispatchTurn(text, attachments, model, agent, contributedInstructions)
      if (!turnId) throw new Error('active relay did not return a dispatched turn id')
      return turnId
    },
    queueMessage: (
      text: string,
      attachments?: MessageAttachment[],
      model?: ModelSelection,
      agent?: AgentSelection,
      contributedInstructions?: ContributedInstructionsMetadata,
    ) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.queueMessage) throw new Error('active relay cannot queue messages')
      return contributedInstructions === undefined
        ? current.runtime.queueMessage(text, attachments, model, agent)
        : current.runtime.queueMessage(text, attachments, model, agent, contributedInstructions)
    },
    steerMessage: (
      text: string,
      attachments?: MessageAttachment[],
      model?: ModelSelection,
      steeringId?: string,
      agent?: AgentSelection,
      contributedInstructions?: ContributedInstructionsMetadata,
    ) => {
      leaseEntryOrThrow(lease, registry)
      return registry.steerRelayMessage(
        registryKey,
        text,
        attachments,
        model,
        steeringId,
        agent,
        contributedInstructions,
      )
    },
    removeQueuedMessage: (id: string) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.removeQueuedMessage) throw new Error('active relay cannot remove queued messages')
      current.runtime.removeQueuedMessage(id)
    },
    completions: (text: string, offset: number) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.completions) return Promise.resolve([])
      return current.runtime.completions(text, offset)
    },
    cancelTurn: (turnId?: string) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive || !current.runtime.cancelTurn) return false
      const accepted = current.runtime.cancelTurn(turnId)
      if (accepted) {
        const store = registry.getRelayStore(registryKey)
        store?.markTurnCancelled?.(turnId ?? store.getSnapshot()?.activeTurn?.id)
      }
      return accepted
    },
    confirmToolCall: (confirmation: {
      approved: boolean
      toolCallId: string
      turnId: string
      resultConfirmation?: boolean
      selectedOptionId?: string
      forSession?: boolean
      chat?: URI
    }) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.confirmToolCall) throw new Error('active relay cannot confirm tool calls')
      current.runtime.confirmToolCall(confirmation)
    },
    completeToolCall: (input: {turnId: string; toolCallId: string; result: ToolCallResult}) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.completeToolCall) throw new Error('active relay cannot complete tool calls')
      current.runtime.completeToolCall(input)
    },
    renameSession: (title: string, options?: RelaySessionRenameOptions) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.renameSession) throw new Error('active relay cannot rename sessions')
      return current.runtime.renameSession(title, options)
    },
    completeInput: (params: BuildInputCompletedActionParams) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (current.passive) throw new Error('relay connection is read-only')
      if (!current.runtime.completeInput) throw new Error('active relay cannot complete input requests')
      current.runtime.completeInput(params)
    },
    setApproveAll: (enabled: boolean) => {
      leaseEntryOrThrow(lease, registry)
      return registry.setRelayApproveAll(registryKey, enabled)
    },
    setApprovalMode: (mode: SessionApprovalMode) => {
      leaseEntryOrThrow(lease, registry)
      return registry.setRelayApprovalMode(registryKey, mode)
    },
    setMode: (mode: string) => {
      leaseEntryOrThrow(lease, registry)
      return registry.setRelayMode(registryKey, mode)
    },
    ensureLive: (onReconnect?: () => void) => {
      leaseEntryOrThrow(lease, registry)
      return registry.ensureRelayLive(registryKey, onReconnect)
    },
    whenConnected: (timeoutMs?: number, provisioningTimeoutMs?: number, signal?: AbortSignal) => {
      leaseEntryOrThrow(lease, registry)
      return registry.whenRelayConnected(registryKey, timeoutMs, provisioningTimeoutMs, signal)
    },
    observeChat: (channel: string, observer: RelayChatObserver) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.observeChat) throw new Error('relay does not support additional chat subscriptions')
      return current.runtime.observeChat(channel, observer)
    },
    observeChangeset: (channel: string, observer: RelayChangesetObserver) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.observeChangeset) {
        throw new Error('relay does not support additional changeset subscriptions')
      }
      return current.runtime.observeChangeset(channel, observer)
    },
    readResource: async (uri: string) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.readResource) throw new Error('relay does not support resource reads')
      return current.runtime.readResource(uri)
    },
    subscribeTelemetry: (observer: RelayTelemetryObserver) => {
      const current = leaseEntryOrThrow(lease, registry)
      if (!current.runtime.subscribeTelemetry) throw new Error('relay does not support telemetry subscriptions')
      return current.runtime.subscribeTelemetry(observer)
    },
  } as InternalRelayLease

  lease[relayLeaseEntry] = {entry, registryKey, isReleased: () => released}
  return lease
}
